import React, { useContext } from 'react'
import { Button, Modal, NotificationManagerContext, Spinner, Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import { useRouteMatch } from 'react-router-dom'
import { CheckmarkCircledIcon, CloseIcon } from 'assets/icons'
import privacyConstants from 'config/constants/privacy'
import uiConstants from 'config/constants/ui'
import CustomPrivacyEditor from 'src/apps/Initiative/apps/CustomPrivacyEditor'
import ModalRoute from 'src/components/utils/ModalRoute'
import { useUpdateInitiative } from 'src/graphql/hooks'
import { InitiativeHierarchyContext } from 'src/libraries/contexts/InitiativeHierarchy'
import PrivacySelectorDescription from './Description'

const PrivacySelector = ({ onCloseButtonClick }) => {
   const match = useRouteMatch()

   const { initiative } = useContext(InitiativeHierarchyContext)
   const { updateInitiative, mutating } = useUpdateInitiative()
   const { showNotification } = useContext(NotificationManagerContext)

   const privacyOptions = initiative.privacy === 'custom'
      ? privacyConstants
      : privacyConstants.filter(o => o.value !== 'custom')

   const handleSelectPrivacyOption = async (value) => {
      try {
         await updateInitiative({ id: initiative.id }, { privacy: value })
         showNotification({
            message: 'Changes saved successfully',
            title: 'Success!',
            type: 'success'
         })
      } catch (err) {
         showNotification({
            message: err.message || 'An error occurred while saving changes',
            title: 'Error',
            type: 'error'
         })
      }
   }

   return (
      <>
         <View
            borderRadius="5px"
            style={{ overflow: 'hidden' }}>
            <View
               display="flex"
               justifyContent="space-between"
               alignItems="center"
               height="60px"
               padding="10px 20px"
               borderBottom="1px solid #eee">
               <Text weight="medium">
                  Select Privacy
               </Text>
               <View
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  onClick={onCloseButtonClick}>
                  <CloseIcon width="24px" height="24px" color="#666" />
               </View>
            </View>
            <View position="relative">
               <View padding="0px 10px">
                  {privacyOptions.map((option, index) => (
                     <View
                        key={option.name}
                        display="flex"
                        padding="20px"
                        borderTop={index !== 0 && '1px solid #eee'}>
                        <View
                           onClick={() => handleSelectPrivacyOption(option.value)}
                           flex="0 0 auto"
                           marginRight="30px"
                           paddingTop="5px"
                           style={{ cursor: 'pointer' }}>
                           {initiative.privacy === option.value && (
                              <CheckmarkCircledIcon
                                 width="20px"
                                 height="20px"
                                 color={['admin', 'draft'].includes(option.value)
                                    ? '#ffc958'
                                    : '#58afff'
                                 }
                              />
                           )}
                           {initiative.privacy !== option.value && (
                              <View
                                 width="20px"
                                 height="20px"
                                 border="2px solid #ccc"
                                 borderRadius="50%"
                              />
                           )}
                        </View>
                        <View display="flex" flex="1 1 auto">
                           <View>
                              <Text size="2.5" weight="medium">
                                 {option.name}
                              </Text>
                              <Text size="2" color="#666">
                                 <PrivacySelectorDescription privacy={option.value} />
                              </Text>
                           </View>
                           {option.value === 'custom' && (
                              <Button
                                 linkTo={`${match.url}/custom-privacy-editor`}
                                 name="Edit"
                                 color="#eee"
                                 textColor="#222"
                                 size="sm"
                              />
                           )}
                        </View>
                     </View>
                  ))}
               </View>
               {initiative.privacy !== 'custom' && (
                  <View
                     display="flex"
                     justifyContent="flex-end"
                     padding="15px"
                     backgroundColor="#f5f5f5">
                     <Button
                        linkTo={`${match.url}/custom-privacy-editor`}
                        size="sm"
                        name="Advanced..."
                        color="#ddd"
                        textColor="#111"
                     />
                  </View>
               )}
               {mutating && (
                  <View
                     display="flex"
                     justifyContent="center"
                     alignItems="center"
                     position="absolute"
                     top="0px"
                     left="0px"
                     right="0px"
                     bottom="0px"
                     backgroundColor="rgba(255,255,255,0.95)"
                     zIndex="2">
                     <Spinner width="40px" height="40px" color="#aaa" />
                  </View>
               )}
            </View>
         </View>
         <ModalRoute path={`${match.path}/custom-privacy-editor`}>
            {({ open, onCloseComplete, onCloseTrigger }) => (
               <Modal
                  borderRadius="5px"
                  width="100%[a-b] 900px[c-f]"
                  height="100%[a-b] 95%[c-f]"
                  onCloseComplete={onCloseComplete}
                  onCloseTrigger={onCloseTrigger}
                  open={open}
                  zIndex={uiConstants.zIndexes.tmpSettingsModal + 2}>
                  <CustomPrivacyEditor onCloseButtonClick={onCloseTrigger} />
               </Modal>
            )}
         </ModalRoute>
      </>
   )
}

PrivacySelector.propTypes = {
   onCloseButtonClick: PropTypes.func.isRequired
}

export default PrivacySelector
